import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Typography, TablePagination, TextField, useTheme,
  useMediaQuery, CardContent, Stack, InputAdornment, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, Divider, Chip, Checkbox, Collapse
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon,
  Search as SearchIcon, CloudUpload as CloudUploadIcon,
  CloudDownload as CloudDownloadIcon, AttachFile as AttachFileIcon,
  DeleteSweep as DeleteSweepIcon, FilterList as FilterListIcon,
  PlaylistAdd as PlaylistAddIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import articuloXListaPrecioService, {
  ArticuloXListaPrecioPagedResult, ModoPrecioImportacion
} from '../../services/articuloXListaPrecioService';
import listaPrecioService from '../../services/listaPrecioService';
import productoService from '../../services/productoService';
import type { ArticuloXListaPrecio as ArticuloXListaPrecioItem } from '../../services/articuloXListaPrecioService';
import AgregarArticulosMasivoDialog from './AgregarArticulosMasivoDialog';
import * as XLSX from 'xlsx';

interface ValidacionImportacion {
  payloadsValidos: ArticuloXListaPrecioItem[];
  errores: { fila: number; mensaje: string }[];
  totalFilas: number;
}

const normalizeHeader = (value: string) =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '').trim();

const parseNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const normalized = raw.includes(',') && raw.includes('.')
    ? raw.replace(/\./g, '').replace(',', '.').replace(/\s/g, '')
    : raw.replace(',', '.').replace(/\s/g, '');
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

const parseModoPrecio = (value: unknown): ModoPrecioImportacion | null => {
  const raw = String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();
  if (!raw) return null;
  if (raw.includes('PORC')) return 'PORCENTAJE';
  if (raw.includes('PREC')) return 'PRECIO';
  return null;
};

const HEADER_ALIASES = {
  codigoLista: ['codigolista', 'lista'],
  codigoArticulo: ['codigoarticulo', 'articulo'],
  modoPrecio: ['modoprecioporcentaje', 'modoprecio', 'modo'],
  precioVenta: ['precioventa', 'precio'],
  porcentajeAjuste: ['ajuste', 'porcentajeajuste', 'porcentaje'],
  observaciones: ['observaciones', 'observacion'],
  descripcion: ['descripcion'],
};

const REQUIRED_HEADER_GROUPS: Array<keyof typeof HEADER_ALIASES> = [
  'codigoLista', 'codigoArticulo', 'modoPrecio', 'precioVenta', 'porcentajeAjuste',
];

const mergeImportWithExisting = (
  existing: ArticuloXListaPrecioItem,
  incoming: ArticuloXListaPrecioItem
): ArticuloXListaPrecioItem => {
  const usePorcentaje = incoming.porcentaje !== undefined && incoming.porcentaje !== null;
  return {
    ...existing,
    codigoLista: incoming.codigoLista.trim(),
    codigoArticulo: incoming.codigoArticulo.trim(),
    descripcion: incoming.descripcion?.trim() ? incoming.descripcion : existing.descripcion,
    precioVta: usePorcentaje ? undefined : incoming.precioVta,
    precioCosto: existing.precioCosto,
    porcentaje: usePorcentaje ? Number(incoming.porcentaje) : undefined,
  };
};

const itemKey = (a: ArticuloXListaPrecioItem) => `${a.codigoLista}::${a.codigoArticulo}`;

const ArticuloXListaPrecio: React.FC<{ codigoListaFijo?: string }> = ({ codigoListaFijo }) => {
  const embedded = !!codigoListaFijo;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [articulosData, setArticulosData] = useState<ArticuloXListaPrecioPagedResult>({
    items: [], totalCount: 0, pageNumber: 1, pageSize: 10, totalPages: 0
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroCodigoLista, setFiltroCodigoLista] = useState('');
  const [filtroCodigoArticulo, setFiltroCodigoArticulo] = useState('');
  const [filtroRamo, setFiltroRamo] = useState('');
  const [filtroLinea, setFiltroLinea] = useState('');
  const [filtroClase, setFiltroClase] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [openBulkDelete, setOpenBulkDelete] = useState(false);
  const [deletingBulk, setDeletingBulk] = useState(false);
  const [openAgregarMasivo, setOpenAgregarMasivo] = useState(false);

  const [openImportar, setOpenImportar] = useState(false);
  const [validacionImport, setValidacionImport] = useState<ValidacionImportacion | null>(null);
  const [importando, setImportando] = useState(false);

  const fetchArticulos = async (
    pageNumber: number, pageSize: number, search?: string,
    codigoLista?: string, codigoArticulo?: string,
    ramo?: string, linea?: string, clase?: string
  ) => {
    try {
      const response = await articuloXListaPrecioService.getArticuloXListaPrecios({
        pageNumber, pageSize,
        searchTerm: search?.trim() || undefined,
        codigoLista: codigoListaFijo ?? (codigoLista?.trim() || undefined),
        codigoArticulo: codigoArticulo?.trim() || undefined,
        codigoRamo: ramo?.trim() || undefined,
        codigoLinea: linea?.trim() || undefined,
        codigoClase: clase?.trim() || undefined,
      });
      setArticulosData(response);
    } catch (error: any) {
      console.error('Error al cargar artículos por lista:', error);
      enqueueSnackbar('Error al cargar artículos por lista', { variant: 'error' });
      setArticulosData({ items: [], totalCount: 0, pageNumber: 1, pageSize, totalPages: 0 });
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchArticulos(page + 1, rowsPerPage, searchTerm, filtroCodigoLista, filtroCodigoArticulo, filtroRamo, filtroLinea, filtroClase);
    }, 300);
    return () => clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, filtroCodigoLista, filtroCodigoArticulo, filtroRamo, filtroLinea, filtroClase]);

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [page, searchTerm, filtroCodigoLista, filtroCodigoArticulo, filtroRamo, filtroLinea, filtroClase]);

  const clearFilters = () => {
    setFiltroCodigoLista('');
    setFiltroCodigoArticulo('');
    setFiltroRamo('');
    setFiltroLinea('');
    setFiltroClase('');
    setSearchTerm('');
    setPage(0);
  };

  const handleDelete = async (codigoLista: string, codigoArticulo: string) => {
    if (window.confirm('¿Está seguro de eliminar este artículo de la lista?')) {
      try {
        await articuloXListaPrecioService.deleteArticuloXListaPrecio(codigoLista, codigoArticulo);
        enqueueSnackbar('Artículo eliminado exitosamente', { variant: 'success' });
        fetchArticulos(page + 1, rowsPerPage, searchTerm, filtroCodigoLista, filtroCodigoArticulo, filtroRamo, filtroLinea, filtroClase);
      } catch (error: any) {
        enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar artículo', { variant: 'error' });
      }
    }
  };

  const handleToggleSelect = (key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const allOnPageSelected = articulosData.items.length > 0 &&
    articulosData.items.every(a => selectedKeys.has(itemKey(a)));
  const someOnPageSelected = articulosData.items.some(a => selectedKeys.has(itemKey(a)));

  const handleSelectAllOnPage = (checked: boolean) => {
    if (checked) {
      setSelectedKeys(new Set(articulosData.items.map(itemKey)));
    } else {
      setSelectedKeys(new Set());
    }
  };

  const handleBulkDelete = async () => {
    setDeletingBulk(true);
    let ok = 0;
    let errors = 0;
    for (const key of Array.from(selectedKeys)) {
      const separatorIndex = key.indexOf('::');
      const lista = key.substring(0, separatorIndex);
      const articulo = key.substring(separatorIndex + 2);
      try {
        await articuloXListaPrecioService.deleteArticuloXListaPrecio(lista, articulo);
        ok++;
      } catch {
        errors++;
      }
    }
    enqueueSnackbar(
      errors > 0 ? `Eliminados: ${ok}. Errores: ${errors}.` : `${ok} artículos eliminados.`,
      { variant: errors > 0 ? 'warning' : 'success' }
    );
    setSelectedKeys(new Set());
    setOpenBulkDelete(false);
    setDeletingBulk(false);
    fetchArticulos(page + 1, rowsPerPage, searchTerm, filtroCodigoLista, filtroCodigoArticulo, filtroRamo, filtroLinea, filtroClase);
  };

  const mapToExportRow = (a: ArticuloXListaPrecioItem) => ({
    'Código Lista': a.codigoLista,
    'Código Artículo': a.codigoArticulo,
    'Descripción': a.descripcion,
    'Modo (PRECIO/PORCENTAJE)': Number(a.porcentaje || 0) !== 0 ? 'PORCENTAJE' : 'PRECIO',
    'Precio Venta': Number(a.porcentaje || 0) !== 0 ? '' : Number(a.precioVta || 0),
    '% Ajuste': Number(a.porcentaje || 0) !== 0 ? Number(a.porcentaje || 0) : '',
    'Ramo': a.codigoRamo,
    'Línea': a.codigoLinea,
    'Clase': a.codigoClase,
    'Observaciones': ''
  });

  const buildWorkbookWithInstructions = (rows: ArticuloXListaPrecioItem[]) => {
    const dataToExport = rows.map(mapToExportRow);
    const ws = XLSX.utils.json_to_sheet(dataToExport.length > 0 ? dataToExport : [{
      'Código Lista': '', 'Código Artículo': '', 'Descripción': '',
      'Modo (PRECIO/PORCENTAJE)': '', 'Precio Venta': '', '% Ajuste': '',
      'Ramo': '', 'Línea': '', 'Clase': '', 'Observaciones': ''
    }]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ArticuloXListaPrecio');
    const instrucciones = [
      ['Columna', 'Descripción', 'Valores aceptados'],
      ['Código Lista', 'Código de lista de precio', 'Obligatorio'],
      ['Código Artículo', 'Código de artículo', 'Obligatorio'],
      ['Modo (PRECIO/PORCENTAJE)', 'Cómo aplicar el valor', 'PRECIO o PORCENTAJE'],
      ['Precio Venta', 'Precio de venta final', 'Obligatorio si Modo=PRECIO'],
      ['% Ajuste', 'Aumento/descuento sobre precio base', 'Obligatorio si Modo=PORCENTAJE (admite +/-)'],
      ['Observaciones', 'Texto libre', 'Opcional']
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(instrucciones), 'Instrucciones');
    return wb;
  };

  const handleExportExcel = async () => {
    try {
      const pageSize = 50;
      let pageNumber = 1;
      let totalPages = 1;
      const allItems: ArticuloXListaPrecioItem[] = [];

      while (pageNumber <= totalPages) {
        const response = await articuloXListaPrecioService.getArticuloXListaPrecios({
          pageNumber, pageSize,
          searchTerm: searchTerm.trim() || undefined,
          codigoLista: codigoListaFijo ?? (filtroCodigoLista.trim() || undefined),
          codigoArticulo: filtroCodigoArticulo.trim() || undefined,
          codigoRamo: filtroRamo.trim() || undefined,
          codigoLinea: filtroLinea.trim() || undefined,
          codigoClase: filtroClase.trim() || undefined,
        });
        allItems.push(...response.items);
        totalPages = response.totalPages || 1;
        pageNumber++;
      }

      if (allItems.length === 0) {
        enqueueSnackbar('No hay datos para exportar con los filtros seleccionados', { variant: 'warning' });
        return;
      }

      const fileName = codigoListaFijo
        ? `Articulos_${codigoListaFijo}.xlsx`
        : 'Articulos_Lista_Precio.xlsx';
      const wb = buildWorkbookWithInstructions(allItems);
      XLSX.writeFile(wb, fileName);
      enqueueSnackbar(`Archivo exportado exitosamente (${allItems.length} registros)`, { variant: 'success' });
    } catch {
      enqueueSnackbar('Error al exportar el archivo', { variant: 'error' });
    }
  };

  const handleDescargarPlantilla = () => {
    const data = [['Código Lista', 'Código Artículo', 'Modo (PRECIO/PORCENTAJE)', 'Precio Venta', '% Ajuste', 'Observaciones'], ['', '', '', '', '', '']];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(data), 'ArticuloXListaPrecio');
    const instrucciones = [
      ['Columna', 'Descripción', 'Valores aceptados'],
      ['Código Lista', 'Código de lista de precio', 'Obligatorio'],
      ['Código Artículo', 'Código de artículo', 'Obligatorio'],
      ['Modo (PRECIO/PORCENTAJE)', 'Cómo aplicar el valor', 'PRECIO o PORCENTAJE'],
      ['Precio Venta', 'Precio de venta final', 'Obligatorio si Modo=PRECIO'],
      ['% Ajuste', 'Aumento/descuento sobre precio base', 'Obligatorio si Modo=PORCENTAJE (admite +/-)'],
      ['Observaciones', 'Texto libre', 'Opcional']
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(instrucciones), 'Instrucciones');
    XLSX.writeFile(wb, 'Plantilla_Articulos_Lista_Precio.xlsx');
    enqueueSnackbar('Plantilla descargada correctamente', { variant: 'success' });
  };

  const getValueByAliases = (row: Record<string, unknown>, aliases: string[]) => {
    for (const [key, value] of Object.entries(row)) {
      if (aliases.includes(normalizeHeader(key))) return value;
    }
    return undefined;
  };

  const validarCatalogos = async (rows: Record<string, unknown>[]) => {
    const codigosLista = new Set<string>();
    const codigosArticulo = new Set<string>();
    rows.forEach((row) => {
      const codigoLista = String(getValueByAliases(row, HEADER_ALIASES.codigoLista) ?? '').trim();
      const codigoArticulo = String(getValueByAliases(row, HEADER_ALIASES.codigoArticulo) ?? '').trim();
      if (codigoLista) codigosLista.add(codigoLista);
      if (codigoArticulo) codigosArticulo.add(codigoArticulo);
    });

    const listasExistentes = new Set<string>();
    const articulosExistentes = new Set<string>();

    await Promise.all(Array.from(codigosLista).map(async (c) => {
      try { await listaPrecioService.getListaPrecio(c); listasExistentes.add(c); } catch { /* no existe */ }
    }));

    await Promise.all(Array.from(codigosArticulo).map(async (c) => {
      try { await productoService.getProducto(c); articulosExistentes.add(c); } catch { /* no existe */ }
    }));

    return { listasExistentes, articulosExistentes };
  };

  const validarYConstruirPayloads = async (rows: Record<string, unknown>[]) => {
    const errores: { fila: number; mensaje: string }[] = [];
    const payloadsPorCombinacion = new Map<string, ArticuloXListaPrecioItem>();
    const { listasExistentes, articulosExistentes } = await validarCatalogos(rows);

    rows.forEach((row, index) => {
      const fila = index + 2;
      const codigoLista = String(getValueByAliases(row, HEADER_ALIASES.codigoLista) ?? '').trim();
      const codigoArticulo = String(getValueByAliases(row, HEADER_ALIASES.codigoArticulo) ?? '').trim();
      const modoPrecio = parseModoPrecio(getValueByAliases(row, HEADER_ALIASES.modoPrecio));
      const precioVenta = parseNumber(getValueByAliases(row, HEADER_ALIASES.precioVenta));
      const porcentajeAjuste = parseNumber(getValueByAliases(row, HEADER_ALIASES.porcentajeAjuste));
      const descripcion = String(getValueByAliases(row, HEADER_ALIASES.descripcion) ?? '').trim();

      if (!codigoLista) { errores.push({ fila, mensaje: 'Código Lista es obligatorio' }); return; }
      if (!codigoArticulo) { errores.push({ fila, mensaje: 'Código Artículo es obligatorio' }); return; }
      if (!listasExistentes.has(codigoLista)) { errores.push({ fila, mensaje: `Código Lista "${codigoLista}" no existe` }); return; }
      if (!articulosExistentes.has(codigoArticulo)) { errores.push({ fila, mensaje: `Código Producto "${codigoArticulo}" no existe` }); return; }
      if (!modoPrecio) { errores.push({ fila, mensaje: 'Modo debe ser PRECIO o PORCENTAJE' }); return; }
      if (modoPrecio === 'PRECIO' && (precioVenta === null || precioVenta <= 0)) { errores.push({ fila, mensaje: 'Precio Venta debe ser > 0 cuando Modo=PRECIO' }); return; }
      if (modoPrecio === 'PORCENTAJE' && porcentajeAjuste === null) { errores.push({ fila, mensaje: '% Ajuste debe ser numérico cuando Modo=PORCENTAJE' }); return; }

      const key = `${codigoLista}::${codigoArticulo}`;
      payloadsPorCombinacion.set(key, {
        codigoLista, codigoArticulo, descripcion,
        precioVta: modoPrecio === 'PRECIO' ? Number(precioVenta) : undefined,
        precioCosto: 0, propio: 'N', iva: 'S', fleteFp: 'N',
        porcentaje: modoPrecio === 'PORCENTAJE' ? Number(porcentajeAjuste) : undefined,
        codigoRamo: '', nombreRamo: '', codigoLinea: '', nombreLinea: '', codigoClase: '', nombreClase: ''
      });
    });

    return { payloadsValidos: Array.from(payloadsPorCombinacion.values()), errores };
  };

  const onImportChangeArticulos = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';
    setValidacionImport(null);

    try {
      const fileBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(fileBuffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(sheet, { defval: '', header: 0 });

      if (!rows.length) { enqueueSnackbar('El archivo está vacío', { variant: 'warning' }); return; }

      const headers = Object.keys(rows[0]).map(normalizeHeader);
      const columnasFaltantes = REQUIRED_HEADER_GROUPS.filter(
        (group) => !HEADER_ALIASES[group].some((alias) => headers.includes(alias))
      );
      if (columnasFaltantes.length > 0) {
        enqueueSnackbar(`Faltan columnas: ${columnasFaltantes.join(', ')}`, { variant: 'error' });
        return;
      }

      const { payloadsValidos, errores } = await validarYConstruirPayloads(rows);
      setValidacionImport({ payloadsValidos, errores, totalFilas: rows.length });
      enqueueSnackbar(
        errores.length > 0
          ? `${payloadsValidos.length} filas válidas y ${errores.length} con errores`
          : `Validación exitosa: ${payloadsValidos.length} filas listas para importar`,
        { variant: payloadsValidos.length > 0 ? 'info' : 'warning' }
      );
    } catch {
      enqueueSnackbar('Error al procesar el archivo Excel', { variant: 'error' });
    }
  };

  const confirmarEImportar = async () => {
    if (!validacionImport || validacionImport.payloadsValidos.length === 0) return;
    setImportando(true);
    let creados = 0, actualizados = 0, fallos = 0;

    for (const payload of validacionImport.payloadsValidos) {
      try {
        let existing: ArticuloXListaPrecioItem | null = null;
        try {
          existing = await articuloXListaPrecioService.getArticuloXListaPrecio(payload.codigoLista, payload.codigoArticulo);
        } catch (err: unknown) {
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (status !== 404) throw err;
        }
        if (existing) {
          await articuloXListaPrecioService.updateArticuloXListaPrecio(payload.codigoLista, payload.codigoArticulo, mergeImportWithExisting(existing, payload));
          actualizados++;
        } else {
          await articuloXListaPrecioService.createArticuloXListaPrecio(payload);
          creados++;
        }
      } catch { fallos++; }
    }

    enqueueSnackbar(
      fallos > 0
        ? `Importación finalizada. Creados: ${creados}. Actualizados: ${actualizados}. Fallos: ${fallos}.`
        : `Importación finalizada. Creados: ${creados}. Actualizados: ${actualizados}.`,
      { variant: fallos > 0 ? 'warning' : 'success' }
    );
    await fetchArticulos(page + 1, rowsPerPage, searchTerm, filtroCodigoLista, filtroCodigoArticulo, filtroRamo, filtroLinea, filtroClase);
    setValidacionImport(null);
    setOpenImportar(false);
    setImportando(false);
  };

  const cerrarDialogImportar = () => {
    setOpenImportar(false);
    setValidacionImport(null);
    setImportando(false);
  };

  const nuevoArticuloPath = codigoListaFijo
    ? `/ventas/articuloxlistaprecio/${codigoListaFijo}/nuevo`
    : '/ventas/articuloxlistaprecio/nuevo';

  const hasAdvancedFilters = !!(filtroRamo || filtroLinea || filtroClase);

  const actionBar = (
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
      {!embedded && (
        <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
          Artículos por Lista de Precio
        </Typography>
      )}
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={embedded ? { ml: 'auto' } : {}}>
        {embedded && (
          <Button
            variant="outlined"
            startIcon={<PlaylistAddIcon />}
            onClick={() => setOpenAgregarMasivo(true)}
            size="small"
          >
            Agregar masivo
          </Button>
        )}
        <Button variant="outlined" startIcon={<AttachFileIcon />} onClick={() => setOpenImportar(true)} size="small">
          Importar
        </Button>
        <Button variant="outlined" startIcon={<CloudDownloadIcon />} onClick={handleExportExcel} size="small">
          Exportar
        </Button>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate(nuevoArticuloPath)} size="small">
          Nuevo
        </Button>
      </Stack>
    </Stack>
  );

  const filterSection = (
    <Box sx={{ mb: 2 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            fullWidth
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#1976d2' }} /></InputAdornment>
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
          />
          {!embedded && (
            <TextField
              label="Código Lista"
              value={filtroCodigoLista}
              onChange={(e) => { setFiltroCodigoLista(e.target.value); setPage(0); }}
              sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          )}
          <TextField
            label="Código Artículo"
            value={filtroCodigoArticulo}
            onChange={(e) => { setFiltroCodigoArticulo(e.target.value); setPage(0); }}
            sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
          />
          <Button
            variant={hasAdvancedFilters ? 'contained' : 'outlined'}
            startIcon={<FilterListIcon />}
            onClick={() => setShowAdvancedFilters(v => !v)}
            sx={{ minWidth: 60, whiteSpace: 'nowrap' }}
            size="small"
            color={hasAdvancedFilters ? 'primary' : 'inherit'}
          >
            Filtros
          </Button>
          <Button variant="outlined" onClick={clearFilters} sx={{ minWidth: 120, whiteSpace: 'nowrap' }} size="small">
            Limpiar
          </Button>
        </Stack>

        <Collapse in={showAdvancedFilters}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Código Ramo"
              value={filtroRamo}
              onChange={(e) => { setFiltroRamo(e.target.value); setPage(0); }}
              fullWidth
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
            <TextField
              label="Código Línea"
              value={filtroLinea}
              onChange={(e) => { setFiltroLinea(e.target.value); setPage(0); }}
              fullWidth
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
            <TextField
              label="Código Clase"
              value={filtroClase}
              onChange={(e) => { setFiltroClase(e.target.value); setPage(0); }}
              fullWidth
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Stack>
        </Collapse>
      </Stack>
    </Box>
  );

  const bulkToolbar = selectedKeys.size > 0 && (
    <Box sx={{ mb: 1, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Typography variant="body2" fontWeight={600}>{selectedKeys.size} seleccionado{selectedKeys.size !== 1 ? 's' : ''}</Typography>
      <Button
        variant="contained"
        color="error"
        size="small"
        startIcon={<DeleteSweepIcon />}
        onClick={() => setOpenBulkDelete(true)}
      >
        Eliminar seleccionados
      </Button>
      <Button variant="outlined" size="small" onClick={() => setSelectedKeys(new Set())}>
        Deseleccionar
      </Button>
    </Box>
  );

  const tableContent = (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={someOnPageSelected && !allOnPageSelected}
                checked={allOnPageSelected}
                onChange={(e) => handleSelectAllOnPage(e.target.checked)}
                size="small"
              />
            </TableCell>
            {!embedded && <TableCell sx={{ fontWeight: 600 }}>Lista</TableCell>}
            <TableCell sx={{ fontWeight: 600 }}>Artículo</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Precio Venta</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>%</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Ramo / Línea</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {articulosData.items.map((articulo) => {
            const key = itemKey(articulo);
            return (
              <TableRow key={key} hover selected={selectedKeys.has(key)}>
                <TableCell padding="checkbox">
                  <Checkbox checked={selectedKeys.has(key)} onChange={() => handleToggleSelect(key)} size="small" />
                </TableCell>
                {!embedded && <TableCell>{articulo.codigoLista}</TableCell>}
                <TableCell>{articulo.codigoArticulo}</TableCell>
                <TableCell>{articulo.descripcion}</TableCell>
                <TableCell>{Number(articulo.porcentaje || 0) !== 0 ? '—' : `$${Number(articulo.precioVta ?? 0).toFixed(2)}`}</TableCell>
                <TableCell>{Number(articulo.porcentaje || 0) !== 0 ? `${Number(articulo.porcentaje)}%` : '—'}</TableCell>
                <TableCell>
                  <Typography variant="caption" display="block">{articulo.codigoRamo || '—'}</Typography>
                  <Typography variant="caption" display="block" color="text.secondary">{articulo.codigoLinea || ''}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => navigate(`/ventas/articuloxlistaprecio/${articulo.codigoLista}/${articulo.codigoArticulo}`)} color="primary">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar">
                    <IconButton size="small" onClick={() => handleDelete(articulo.codigoLista, articulo.codigoArticulo)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={articulosData.totalCount}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      />
    </TableContainer>
  );

  const mobileContent = (
    <Stack spacing={2}>
      {articulosData.items.map((articulo) => {
        const key = itemKey(articulo);
        return (
          <Card key={key} variant="outlined" sx={{ p: 2, bgcolor: selectedKeys.has(key) ? '#e3f2fd' : undefined }}>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Checkbox checked={selectedKeys.has(key)} onChange={() => handleToggleSelect(key)} size="small" />
                <Typography variant="subtitle1" fontWeight={600}>
                  {articulo.codigoArticulo} — {articulo.descripcion}
                </Typography>
              </Stack>
              {!embedded && <Typography variant="body2" color="text.secondary">Lista: {articulo.codigoLista}</Typography>}
              <Typography variant="body2" color="text.secondary">
                {Number(articulo.porcentaje || 0) !== 0 ? `% ${Number(articulo.porcentaje)}` : `$${Number(articulo.precioVta ?? 0).toFixed(2)}`}
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <IconButton size="small" onClick={() => navigate(`/ventas/articuloxlistaprecio/${articulo.codigoLista}/${articulo.codigoArticulo}`)} color="primary">
                  <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(articulo.codigoLista, articulo.codigoArticulo)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Stack>
            </Stack>
          </Card>
        );
      })}
    </Stack>
  );

  const dialogs = (
    <>
      {/* Bulk delete confirmation */}
      <Dialog open={openBulkDelete} onClose={() => !deletingBulk && setOpenBulkDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar eliminación masiva</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Eliminar <strong>{selectedKeys.size}</strong> artículo{selectedKeys.size !== 1 ? 's' : ''} de la lista?
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBulkDelete(false)} color="inherit" disabled={deletingBulk}>Cancelar</Button>
          <Button onClick={handleBulkDelete} color="error" variant="contained" disabled={deletingBulk}>
            {deletingBulk ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={openImportar} onClose={cerrarDialogImportar} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 30 }} />
            <Typography variant="h6" fontWeight={700}>Importar Artículos por Lista</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {!validacionImport ? (
            <>
              <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, mb: 3, p: 3, background: '#fafbfd' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CloudDownloadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                  <Typography component="span" fontWeight={700} fontSize={18}>1. Descargar Plantilla</Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Descargá la plantilla con el formato recomendado.
                </Typography>
                <Button variant="contained" color="info" fullWidth sx={{ fontWeight: 700, py: 1.2 }} startIcon={<CloudDownloadIcon />} onClick={handleDescargarPlantilla}>
                  Descargar Plantilla
                </Button>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, p: 3, background: '#fafbfd' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CloudUploadIcon color="success" sx={{ fontSize: 30, mr: 1 }} />
                  <Typography component="span" fontWeight={700} fontSize={18}>2. Validar Archivo</Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Seleccioná tu Excel. Se validará antes de importar.
                </Typography>
                <label htmlFor="excel-upload-articulos-lista">
                  <input id="excel-upload-articulos-lista" type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={onImportChangeArticulos} />
                  <Button variant="contained" fullWidth color="success" startIcon={<CloudUploadIcon />} component="span" sx={{ fontWeight: 700, py: 1.2 }}>
                    Seleccionar y validar archivo
                  </Button>
                </label>
              </Box>
            </>
          ) : (
            <Box sx={{ py: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Resultado de la validación</Typography>
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip label={`Total filas: ${validacionImport.totalFilas}`} color="default" size="small" />
                  <Chip label={`Válidos: ${validacionImport.payloadsValidos.length}`} color="success" size="small" />
                  {validacionImport.errores.length > 0 && <Chip label={`Con errores: ${validacionImport.errores.length}`} color="error" size="small" />}
                </Box>
              </Stack>
              {validacionImport.errores.length > 0 && (
                <Box sx={{ maxHeight: 180, overflow: 'auto', mb: 2, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1 }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Filas con errores (no se importarán):
                  </Typography>
                  {validacionImport.errores.slice(0, 15).map((error, idx) => (
                    <Typography key={idx} variant="body2" sx={{ display: 'block' }}>• Fila {error.fila}: {error.mensaje}</Typography>
                  ))}
                  {validacionImport.errores.length > 15 && (
                    <Typography variant="body2" color="text.secondary">... y {validacionImport.errores.length - 15} más</Typography>
                  )}
                </Box>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {validacionImport.payloadsValidos.length > 0
                  ? `Presioná "Confirmar e importar" para procesar ${validacionImport.payloadsValidos.length} registros válidos.`
                  : 'No hay registros válidos. Corregí el archivo y volvé a validar.'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {validacionImport ? (
            <>
              <Button onClick={() => setValidacionImport(null)} color="inherit" disabled={importando}>Volver a seleccionar</Button>
              <Button variant="contained" color="primary" onClick={confirmarEImportar} disabled={importando || validacionImport.payloadsValidos.length === 0}>
                {importando ? 'Importando...' : `Confirmar e importar ${validacionImport.payloadsValidos.length}`}
              </Button>
            </>
          ) : (
            <Button onClick={cerrarDialogImportar} color="inherit">Cancelar</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Agregar masivo */}
      {embedded && codigoListaFijo && (
        <AgregarArticulosMasivoDialog
          open={openAgregarMasivo}
          onClose={() => setOpenAgregarMasivo(false)}
          codigoListaDestino={codigoListaFijo}
          onSuccess={() => fetchArticulos(page + 1, rowsPerPage, searchTerm, filtroCodigoLista, filtroCodigoArticulo, filtroRamo, filtroLinea, filtroClase)}
        />
      )}
    </>
  );

  const mainContent = (
    <>
      {actionBar}
      {filterSection}
      {bulkToolbar}
      {isMobile ? mobileContent : tableContent}
      {dialogs}
    </>
  );

  if (embedded) {
    return <Box>{mainContent}</Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {mainContent}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ArticuloXListaPrecio;
